import React, { useEffect, useState } from "react";
import {
    WiDaySunny,
    WiDayCloudy,
    WiCloudy,
    WiDayRain,
    WiRain,
    WiSnow,
    WiDayRainMix,
    WiDayCloudyWindy,
    WiDayHaze,
    WiFog,
    WiNightClear,
    WiNightAltCloudy,
    WiNightAltRain,
    WiNightAltRainMix,
    WiNightAltCloudyWindy,
    WiNightFog,
    WiNightAltHail
} from "react-icons/wi";

import us from '../assets/country/us.png';
import br from '../assets/country/br.png';
import ar from '../assets/country/ar.png';
import gb from '../assets/country/gb.png';
import mx from '../assets/country/mx.png';
import au from '../assets/country/au.png';

export default function Location({ title, city, utcOffset, countryCode, lat, lon }) {
    const [now, setNow] = useState(new Date());
    const [weather, setWeather] = useState(null);
    const [forecast, setForecast] = useState([]);
    const [lastWeatherRefresh, setLastWeatherRefresh] = useState(null); // <-- Add this line

    const apiKey = "ac27863b3b5153c1f44561d4675fb406";

    // ✅ Country image map
    const countryImages = {
        us,
        br,
        ar,
        gb,
        mx,
        au,
    };

    const flagImage = countryCode ? countryImages[countryCode.toLowerCase()] : null;

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        async function fetchWeather() {
            try {
                const response = await fetch(
                    `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(city)}&appid=${apiKey}&units=imperial`
                );
                if (!response.ok) {
                    // Handle 404 or other errors
                    setWeather(null);
                    setLastWeatherRefresh(null);
                    console.error(`Weather fetch failed: ${response.status} ${response.statusText}`);
                    return;
                }
                const data = await response.json();
                setWeather(data);
                setLastWeatherRefresh(new Date());
            } catch (error) {
                setWeather(null);
                setLastWeatherRefresh(null);
                console.error("Failed to fetch weather:", error);
            }
        }

        fetchWeather();
        const interval = setInterval(fetchWeather, 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, [city]);

    useEffect(() => {
        async function fetchForecast() {
            try {
                const response = await fetch(
                    `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${apiKey}&units=imperial`
                );
                const data = await response.json();

                const dailyTemps = {};

                data.list.forEach(entry => {
                    const date = entry.dt_txt.split(' ')[0];
                    const tempMin = entry.main.temp_min;
                    const tempMax = entry.main.temp_max;

                    if (!dailyTemps[date]) {
                        dailyTemps[date] = { lows: [], highs: [] };
                    }

                    dailyTemps[date].lows.push(tempMin);
                    dailyTemps[date].highs.push(tempMax);
                });

                const parsedForecast = Object.entries(dailyTemps).map(([date, temps]) => ({
                    date,
                    low: Math.min(...temps.lows).toFixed(1),
                    high: Math.max(...temps.highs).toFixed(1),
                }));

                setForecast(parsedForecast.slice(0, 5)); // Only next 5 days
            } catch (error) {
                console.error("Failed to fetch forecast:", error);
            }
        }

        fetchForecast();
    }, [lat, lon]);

    const offsetMs = (utcOffset ?? 0) * 60 * 60 * 1000;
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const localTime = new Date(utc + offsetMs);

    const formattedDate = localTime.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    const formattedTime = localTime.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    const referenceOffset = -7;
    const offsetDiff = (utcOffset ?? 0) - referenceOffset;
    let offsetLabel = "";
    if (offsetDiff > 0) {
        offsetLabel = `${offsetDiff} hour${offsetDiff !== 1 ? "s" : ""} ahead.`;
    } else if (offsetDiff < 0) {
        offsetLabel = `${Math.abs(offsetDiff)} hour${offsetDiff !== -1 ? "s" : ""} behind.`;
    } else {
        offsetLabel = "";
    }

    function getWeatherIcon(condition, hour) {
        const isNight = hour >= 18 || hour < 6;
        switch (condition.toLowerCase()) {
            case "clear":
            case "sunny":
                return isNight
                    ? <WiNightClear style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiDaySunny style={{ fontSize: "4em", color: "#f7b733", margin: "8px 0" }} />;
            case "clouds":
                return isNight
                    ? <WiNightAltCloudy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiCloudy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "partly cloudy":
            case "few clouds":
            case "scattered clouds":
                return isNight
                    ? <WiNightAltCloudy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiDayCloudy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "rain":
            case "shower rain":
                return isNight
                    ? <WiNightAltRain style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiRain style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "snow":
                return isNight
                    ? <WiNightAltHail style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiSnow style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "rain and sun":
            case "sun shower":
            case "light rain":
            case "moderate rain":
                return isNight
                    ? <WiNightAltRainMix style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiDayRainMix style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "smoke":
            case "dust":
            case "haze":
                return isNight
                    ? <WiDayHaze style={{ fontSize: "4em", color: "#888", margin: "8px 0", opacity: 0.7 }} />
                    : <WiDayHaze style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "fog":
                return isNight
                    ? <WiNightFog style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiFog style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            case "windy":
                return isNight
                    ? <WiNightAltCloudyWindy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiDayCloudyWindy style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
            default:
                return isNight
                    ? <WiNightClear style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />
                    : <WiDaySunny style={{ fontSize: "4em", color: "#888", margin: "8px 0" }} />;
        }
    }

    return (
        <div
            style={{
                position: "relative",
                padding: "20px",
                borderRadius: "8px",
                backgroundColor: "rgba(255,255,255,0.7)",
                width: "400px",
                margin: "0 auto",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                color: "#575252",
            }}
        >
            <div style={{color: "#5491aa", fontSize: "1.7em", fontWeight: "bold"}}>{title}</div>
            <div style={{paddingBottom: "10px"}}>{city}</div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ flex: "0 0 auto", fontSize: "2.0em", fontWeight: "bold", marginRight: "18px"}}>
                    {formattedTime}
                </div>
                <div style={{ flex: "1 1 0" }}>
                    <div style={{ fontSize: "1.2em", color: "#575252" }}>{formattedDate}</div>
                    <div style={{ fontSize: "0.95em", color: "#575252" }}>{offsetLabel}</div>
                </div>
            </div>
            {weather && weather.weather && Array.isArray(weather.weather) && weather.weather[0] && forecast.length > 0 ? (
                <>
                    <div style={{ marginTop: "10px", display: "flex", alignItems: "center" }}>
                        <div style={{ flex: "0 0 auto", marginRight: "5px" }}>
                            {getWeatherIcon(weather.weather[0].main, localTime.getHours())}
                        </div>
                        <div style={{ flex: "0 0 auto", marginRight: "18px", fontSize: "2em", fontWeight: "bold" }}>
                            {Math.round(weather.main.temp)}°F
                        </div>
                        <div style={{ flex: "1 1 0" }}>
                            <div>Conditions:<strong> {weather.weather[0].main}</strong></div>
                            <div>
                                H: <strong>{Math.round(forecast[1].high)}°F </strong>/ L: <strong>{Math.round(forecast[1].low)}°F</strong> 
                            </div>
                        </div>
                    </div>
                    {lastWeatherRefresh && (
                        <div style={{ fontSize: "0.8em", color: "#888", marginTop: "4px", fontStyle: "italic" }}>
                            Weather updated: {lastWeatherRefresh.toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit", second: "2-digit" })}
                        </div>
                    )}
                </>
            ) : (
                <div>Loading weather...</div>
            )}

            {/* ✅ Country flag background image */}
            {flagImage && (
                <img
                    src={flagImage}
                    alt={countryCode}
                    style={{
                        position: "absolute",
                        width: "100px",
                        height: "100px",
                        bottom: "10px",
                        right: "10px",
                        opacity: 0.35,
                        pointerEvents: "none",
                        userSelect: "none",
                    }}
                />
            )}
        </div>
    );
}

